import { useCallback, useMemo, useState } from "react";
import { useAuth } from "../../../../../contexts/AuthProvider";
import { useUserSettingsContext } from "../../../../../contexts/Staffing/UserSettingsContext";
import {
	type PersistedWeekScheduleExportSettings,
	type WeekStaffScheduleExportSettings as Settings,
	toPanelSettings,
	toPersistedSettings,
} from "../../../../../downloads/weekStaffScheduleExport/persistedSettings";
import { useDebouncedSave } from "../../../../../hooks/useDebouncedSave";
import { useRestoreUserSettings } from "../../../../../hooks/useUserSettings";
import type { UserSettings } from "../../../../../utils/models";
import { EXPORT_SETTINGS_SAVE_DEBOUNCE_MS } from "./consts";

/** The one column this panel owns, for the field-scoped restore endpoint. */
const RESTORE_FIELDS = ["week_staff_schedule_export_settings"] satisfies (keyof UserSettings)[];

/** One debounce timer for the whole panel — the last payload wins, whichever control produced it. */
const SAVE_KEY = "weekScheduleExport";

type Params = {
	/** Reported alongside every save/reset so the events are attributable. */
	onSaved: (persisted: PersistedWeekScheduleExportSettings) => void;
	onResetToDepartmentDefault: () => void;
	onSavedAsDepartmentDefault: () => void;
	onError: (error: unknown) => void;
};

/**
 * Owns the export panel's settings and their persistence to
 * `user_settings.week_staff_schedule_export_settings`.
 *
 * Local state is **nullable, and null means "untouched — follow the server"**. That removes the need
 * for a hydration effect and the usual `hasInteracted` guard: if the settings query resolves after
 * this mounts, an untouched panel picks the server value up on the next render, and once the user
 * changes anything their value takes over until they reset.
 */
export const useWeekStaffScheduleExportSettings = ({ onSaved, onResetToDepartmentDefault, onSavedAsDepartmentDefault, onError }: Params) => {
	const { settings: userSettings, updateSettings } = useUserSettingsContext();
	const { mutateAsync: restoreUserSettings } = useRestoreUserSettings();
	const { userInfo } = useAuth();
	const [localPersisted, setLocalPersisted] = useState<PersistedWeekScheduleExportSettings | null>(null);

	const persisted = localPersisted ?? userSettings.weekScheduleExport;

	const scheduleSave = useDebouncedSave<PersistedWeekScheduleExportSettings>((payload) => {
		updateSettings({ weekScheduleExport: payload })
			.then(() => onSaved(payload))
			.catch(onError);
	}, EXPORT_SETTINGS_SAVE_DEBOUNCE_MS);

	/** Take ownership of the document locally, then persist it. */
	const commit = useCallback(
		(next: PersistedWeekScheduleExportSettings) => {
			setLocalPersisted(next);
			scheduleSave(SAVE_KEY, next);
		},
		[scheduleSave],
	);

	const settings = useMemo<Settings>(() => toPanelSettings(persisted), [persisted]);

	const updateSetting = useCallback(
		<K extends keyof Settings>(key: K, value: Settings[K]) => {
			commit(toPersistedSettings({ ...settings, [key]: value }, persisted.columnWidths));
		},
		[commit, settings, persisted.columnWidths],
	);

	/**
	 * No-ops when the widths match what is already stored.
	 *
	 * The preview widget reports its column lengths on every change event, most of which are not width
	 * changes; without this guard each one would schedule a save and rebuild the workbook.
	 */
	const setColumnWidths = useCallback(
		(columnWidths: Record<string, number>) => {
			const current = persisted.columnWidths;
			const keys = Object.keys(columnWidths);
			if (keys.length === Object.keys(current).length && keys.every((key) => current[key] === columnWidths[key])) return;
			commit({ ...persisted, columnWidths });
		},
		[commit, persisted],
	);

	/**
	 * Back to the department default.
	 *
	 * Dropping local state is what makes this work: the restore mutation writes its response into the
	 * settings query cache, so `userSettings.weekScheduleExport` becomes the department default and the
	 * now-untouched panel follows it. Re-scheduling the restored document afterwards supersedes any
	 * in-flight debounced save under the same key (which would otherwise land after the restore and
	 * undo it); the write itself is a no-op.
	 */
	const resetToDepartmentDefault = useCallback(async (): Promise<void> => {
		try {
			const restored = await restoreUserSettings(RESTORE_FIELDS);
			setLocalPersisted(null);
			scheduleSave(SAVE_KEY, restored.week_staff_schedule_export_settings);
			onResetToDepartmentDefault();
		} catch (error) {
			// Leave local state alone on failure, so the panel keeps showing what the user actually has.
			onError(error);
		}
	}, [restoreUserSettings, scheduleSave, onResetToDepartmentDefault, onError]);

	/**
	 * Push the current document as the department default, propagating it onto every user row.
	 *
	 * Support-only by product decision — deliberately NOT `useSaveAsDefaultEnabled`, which also honours
	 * the `save_as_default_enabled` flag. This is a UI gate; the backend independently requires
	 * support-or-flag plus the head-nurse role.
	 */
	const canSaveAsDepartmentDefault = userInfo.support;

	const saveAsDepartmentDefault = useCallback(async (): Promise<void> => {
		if (!canSaveAsDepartmentDefault) return;
		try {
			// Only this key is populated, so the PUT carries only this column.
			await updateSettings({ weekScheduleExport: persisted }, true);
			onSavedAsDepartmentDefault();
		} catch (error) {
			onError(error);
		}
	}, [canSaveAsDepartmentDefault, updateSettings, persisted, onSavedAsDepartmentDefault, onError]);

	return {
		settings,
		/** Absent until the user drags a column — the builder's "no overrides" contract. */
		columnWidthOverrides: Object.keys(persisted.columnWidths).length > 0 ? persisted.columnWidths : undefined,
		updateSetting,
		setColumnWidths,
		resetToDepartmentDefault,
		canSaveAsDepartmentDefault,
		saveAsDepartmentDefault,
	};
};
